import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useDataService } from './ApiDataService';
import { useErrorWrapper } from './FormErrorService';

export function useFindById(callerConfig) {
    const dataService = useDataService();
    const [finding, setFinding] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [reloadTrigger, setReloadTrigger] = useState();
    const [formData, setFormData] = useState({});
    const { errorWrapper } = useErrorWrapper();
    const funcConfig = _.defaults(callerConfig, {
        isV3Api: false,
        postFindByIdHandler: (response) => response,
        defaultValues: {},
        errorHandler: errorWrapper(),
    });

    const resetForm = () => setReloadTrigger(_.uniqueId());

    const queryStart = () => {
        setFinding(true);
    };

    const queryEnd = () => {
        setFinding(false);
        setInitialized(true);
    };

    useEffect(() => {
        if (!funcConfig.recordId) {
            Promise.resolve(queryStart())
                .then(() => {
                    setFormData(funcConfig.defaultValues);
                    funcConfig.resetFunc(funcConfig.defaultValues);
                })
                .finally(queryEnd);
        } else {
            Promise.resolve(queryStart())
                .then(() =>
                    dataService.get(`${funcConfig.url}${funcConfig.recordId}${funcConfig.isV3Api ? '/details' : ''}`)
                )
                .then(funcConfig.postFindByIdHandler)
                .then((response) => {
                    setFormData(funcConfig.isV3Api ? response.data : response);
                    funcConfig.resetFunc(funcConfig.isV3Api ? response.data : response);
                })
                .catch(funcConfig.errorHandler)
                .finally(queryEnd);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger]);

    return { initialized, finding, formData, resetForm };
}

export function useFind(callerConfig) {
    const dataService = useDataService();
    const [finding, setFinding] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [formData, setFormData] = useState({});
    const [reloadTrigger, setReloadTrigger] = useState();
    const { errorWrapper } = useErrorWrapper();
    const funcConfig = _.defaults(callerConfig, {
        postFindHandler: (response) => response,
        defaultValues: {},
        errorHandler: errorWrapper(),
    });

    const resetForm = () => setReloadTrigger(_.uniqueId());

    const func = callerConfig.queryFunc ? callerConfig.queryFunc : () => dataService.get(`${funcConfig.url}`);

    const queryStart = () => {
        setFinding(true);
    };

    const queryEnd = () => {
        setFinding(false);
        setInitialized(true);
    };

    useEffect(() => {
        Promise.resolve(queryStart())
            .then(func)
            .then((response) => _.defaults({}, response, funcConfig.defaultValues))
            .then(funcConfig.postFindHandler)
            .then((response) => {
                setFormData(response);
                funcConfig.resetFunc(response);
            })
            .catch(funcConfig.errorHandler)
            .finally(queryEnd);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger]);

    return { initialized, finding, formData, resetForm };
}

function sortTags(tags) {
    if (!tags || !tags.data) {
        return [];
    }

    if (!_.isArray(tags.data)) {
        return [];
    }

    return _.orderBy(tags.data, [(tag) => (tag || '').toLowerCase()], ['asc']);
}

export function useTags(callerConfig) {
    const dataService = useDataService();
    const [tags, setTags] = useState([]);
    const funcConfig = _.defaults(callerConfig, {});

    useEffect(() => {
        dataService
            .get(funcConfig.url)
            .then((response) => setTags(sortTags(response)))
            .catch(funcConfig.errorHandler);

        // Just need this to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { tags };
}

export function prepareQueryParameters(queryParameters) {
    try {
        return JSON.stringify(queryParameters);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`Query Parameters must be JSON ${e.message}`);
    }

    return '';
}

export default function FormQueryServiceFactory(dataService) {
    function findById(callerConfig) {
        const funcConfig = _.defaults(callerConfig, {
            postFindByIdHandler: (response) => response,
            defaultValues: {},
        });

        if (!funcConfig.recordId) {
            funcConfig.resetFunc(funcConfig.defaultValues);
        }

        return dataService
            .get(`${funcConfig.url}${funcConfig.recordId}`)
            .then(funcConfig.postFindByIdHandler)
            .then((response) => funcConfig.resetFunc(response));
    }

    function queryAPI(url, queryParameters = {}, extraParameters = {}) {
        let apiQueryParameters = '';

        if (queryParameters) {
            apiQueryParameters = prepareQueryParameters(queryParameters);
        }

        return dataService.get(url, { query: apiQueryParameters, ...extraParameters });
    }

    function queryAPIPost(url, postBody = {}, queryParameters = {}, extraParameters = {}) {
        let apiQueryParameters = '';
        if (queryParameters) {
            apiQueryParameters = prepareQueryParameters(queryParameters);
        }

        return dataService.post(url, postBody, { query: apiQueryParameters, ...extraParameters });
    }

    return {
        findById,
        queryAPI,
        queryAPIPost,
    };
}

export function useFormQueryService() {
    const dataService = useDataService();
    return useMemo(() => FormQueryServiceFactory(dataService), [dataService]);
}
